import React, { useState } from 'react';
import { Button, Input } from '@/components/atoms';
import { warehouseFormSchema, type WarehouseFormData } from '@/schemas/warehouse';
import { useCreateWarehouseMutation, useUpdateWarehouseMutation } from '@/hooks/useErpApi';
import type { Warehouse } from '@/schemas/warehouse';

interface WarehouseFormProps {
  initialData?: Warehouse;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const WarehouseForm: React.FC<WarehouseFormProps> = ({
  initialData,
  onSuccess,
  onCancel,
}) => {
  const { mutate: createWarehouse, isLoading: createLoading } = useCreateWarehouseMutation();
  const { mutate: updateWarehouse, isLoading: updateLoading } = useUpdateWarehouseMutation();
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const isLoading = createLoading || updateLoading;

  const [formData, setFormData] = useState<WarehouseFormData>({
    name: initialData?.name || '',
    description: '',
    address: {
      type: 'WORK',
      house: initialData?.address?.house || '',
      street: initialData?.address?.street || '',
      landmark: initialData?.address?.landmark || '',
      post_office: initialData?.address?.post_office || '',
      subdistrict: initialData?.address?.subdistrict || '',
      district: initialData?.address?.district || '',
      vtc: initialData?.address?.vtc || '',
      state: initialData?.address?.state || '',
      country: initialData?.address?.country || 'India',
      pincode: initialData?.address?.pincode || '',
      is_primary: true,
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [name]: value },
    }));
    if (validationErrors[`address.${name}`]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`address.${name}`];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});

    try {
      const validated = warehouseFormSchema.parse(formData);

      const payload = {
        name: validated.name,
        address: validated.address,
      };

      if (initialData) {
        await updateWarehouse({ id: initialData.id, data: payload });
      } else {
        await createWarehouse(payload);
      }

      onSuccess?.();
    } catch (err) {
      if (err && typeof err === 'object' && 'errors' in err) {
        const zodError = err as { errors: Array<{ path: (string | number)[]; message: string }> };
        const errMap: Record<string, string> = {};
        zodError.errors.forEach((e) => {
          const field = e.path.join('.');
          errMap[field] = e.message;
        });
        setValidationErrors(errMap);
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <p className="text-error-700">{error}</p>
        </div>
      )}

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-2">
          Warehouse Name *
        </label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter warehouse name"
          disabled={isLoading}
        />
        {validationErrors.name && (
          <p className="text-error-600 text-sm mt-1">{validationErrors.name}</p>
        )}
      </div>

      <div className="border-t border-neutral-200 pt-4">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Address Information</h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="house" className="block text-sm font-medium text-neutral-700 mb-2">
              Building/House Number
            </label>
            <Input
              id="house"
              name="house"
              value={formData.address.house}
              onChange={handleAddressChange}
              placeholder="Building/House number"
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="street" className="block text-sm font-medium text-neutral-700 mb-2">
              Street *
            </label>
            <Input
              id="street"
              name="street"
              value={formData.address.street}
              onChange={handleAddressChange}
              placeholder="Street name"
              disabled={isLoading}
            />
            {validationErrors['address.street'] && (
              <p className="text-error-600 text-sm mt-1">{validationErrors['address.street']}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label htmlFor="landmark" className="block text-sm font-medium text-neutral-700 mb-2">
              Landmark
            </label>
            <Input
              id="landmark"
              name="landmark"
              value={formData.address.landmark}
              onChange={handleAddressChange}
              placeholder="Nearby landmark"
              disabled={isLoading}
            />
          </div>

          <div>
            <label
              htmlFor="post_office"
              className="block text-sm font-medium text-neutral-700 mb-2"
            >
              Post Office
            </label>
            <Input
              id="post_office"
              name="post_office"
              value={formData.address.post_office}
              onChange={handleAddressChange}
              placeholder="Post office name"
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label htmlFor="vtc" className="block text-sm font-medium text-neutral-700 mb-2">
              City/Town *
            </label>
            <Input
              id="vtc"
              name="vtc"
              value={formData.address.vtc}
              onChange={handleAddressChange}
              placeholder="City or town name"
              disabled={isLoading}
            />
            {validationErrors['address.vtc'] && (
              <p className="text-error-600 text-sm mt-1">{validationErrors['address.vtc']}</p>
            )}
          </div>

          <div>
            <label htmlFor="district" className="block text-sm font-medium text-neutral-700 mb-2">
              District
            </label>
            <Input
              id="district"
              name="district"
              value={formData.address.district}
              onChange={handleAddressChange}
              placeholder="District name"
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <div>
            <label htmlFor="state" className="block text-sm font-medium text-neutral-700 mb-2">
              State *
            </label>
            <Input
              id="state"
              name="state"
              value={formData.address.state}
              onChange={handleAddressChange}
              placeholder="State name"
              disabled={isLoading}
            />
            {validationErrors['address.state'] && (
              <p className="text-error-600 text-sm mt-1">{validationErrors['address.state']}</p>
            )}
          </div>

          <div>
            <label htmlFor="pincode" className="block text-sm font-medium text-neutral-700 mb-2">
              Pincode *
            </label>
            <Input
              id="pincode"
              name="pincode"
              value={formData.address.pincode}
              onChange={handleAddressChange}
              placeholder="6-digit pincode"
              disabled={isLoading}
            />
            {validationErrors['address.pincode'] && (
              <p className="text-error-600 text-sm mt-1">{validationErrors['address.pincode']}</p>
            )}
          </div>

          <div>
            <label htmlFor="country" className="block text-sm font-medium text-neutral-700 mb-2">
              Country
            </label>
            <Input
              id="country"
              name="country"
              value={formData.address.country}
              onChange={handleAddressChange}
              placeholder="Country"
              disabled={isLoading}
            />
          </div>
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-4">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? 'Saving...' : initialData ? 'Update' : 'Create'} Warehouse
        </Button>
      </div>
    </form>
  );
};
